import type { DocumentFont } from '@flobi/artwork-core';
import { documentFontUrls } from '../canvas/documentFonts';
import { readableMediaUrl } from '../services/mediaRead';
import type { VideoSnapshot } from './videoExport';

/** What the editor asks of `exportVideo`. */
export interface VideoOptions {
  readonly artboardIds: readonly string[];
  /** Each artboard's file name, in the same order. */
  readonly names: readonly string[];
  readonly scale: number;
  readonly fps: number;
  readonly format: 'mp4' | 'gif';
}

/** The canvas's snapshot, before its fonts and pictures are given links the export page can read. */
export type RawVideoSnapshot = Omit<VideoSnapshot, 'fonts'> & { fonts: DocumentFont[] };

/**
 * Links for every font face and picture, asked for here in the editor, where the session is: the
 * export page and its worker only fetch them. A WOFF face is refused as a still export refuses it.
 */
export async function prepareVideoSnapshot(raw: RawVideoSnapshot): Promise<VideoSnapshot> {
  const fonts = await Promise.all(
    raw.fonts.flatMap((font) =>
      (font.faces ?? []).map(async (face) => {
        if (face.format === 'woff' || face.format === 'woff2') throw new Error(`The font ${font.family} needs a TTF or OTF file before exporting.`);
        const { url } = await documentFontUrls.urlFor(font, face);
        return { family: font.family, url };
      }),
    ),
  );
  const assets = await Promise.all(
    raw.assets.map(async ([id, asset]): Promise<VideoSnapshot['assets'][number]> => [id, asset.src ? { ...asset, src: await readableMediaUrl(asset.src, asset.mediaAssetId ?? id) } : asset]),
  );
  return { ...raw, fonts, assets };
}
